import { useEffect, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { loadWafScript } from "./utils/awsWafIntegration";
import { SelectedOptinalPreferences, steps } from "./constants/steps";
import { SubmissionCreated } from "./views/SubmissionCreated";
import { Layout } from "./components/Layout";
import { ConnecteeSubmission } from "./types/connecteeSubmission";
import { DevResultScreenSwitcher } from "./dev/DevResultScreenSwitcher";
import { AppContext, ContactInfoErrors, FormData } from "./AppContext";

export type { FormData, ContactInfoErrors, AppContextType } from "./AppContext";
export { AppContext } from "./AppContext";

function App() {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isSubmissionCreated, setIsSubmissionCreated] = useState(false);
  const [submission, setSubmission] = useState<ConnecteeSubmission | null>(null);
  const [form, setForm] = useState<FormData>({
    forWhom: "",
    formattedAddress: "",
    placeId: "",
    location: {},
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    country: "",
    state: "",
    city: "",
    howToContact: "",
    additionalInfo: null,
    providerClinicalSpecialties: [] as string[],
    providerGender: "",
    providerAgeGroups: "",
    providerCertifications: "",
    providerDeliveryOptions: "",
    providerModalities: "",
    providerLanguage: "",
  });
  const [selectedOptionalPreferences, setSelectedOptionalPreferences] = useState<SelectedOptinalPreferences[]>([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [contactInfoErrors, setContactInfoErrors] = useState<ContactInfoErrors>({});

  useEffect(() => {
    loadWafScript().catch(console.error);
  }, []);

  const updateForm = (newData: Partial<FormData>) => {
    setForm((prevData) => ({ ...prevData, ...newData }));
  };

  const handleNextStep = () => {
    setCurrentStep((step: number) => step + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep((step: number) => step - 1);
  };

  const handleNewError = (errMessage: string) => {
    setErrorMessage(errMessage);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMessage("");
  };

  const activeSteps = useMemo(
    () => steps.filter(({ isActive }) => isActive(selectedOptionalPreferences)),
    [selectedOptionalPreferences]
  );

  const CurrentStepComponent = activeSteps[currentStep].component;
  return (
    <AppContext.Provider
      value={{
        activeSteps,
        currentStep,
        setCurrentStep,
        handleNextStep,
        handlePrevStep,
        form,
        updateForm,
        selectedOptionalPreferences,
        setSelectedOptionalPreferences,
        isSubmissionCreated,
        setIsSubmissionCreated,
        submission,
        setSubmission,
        handleNewError,
        contactInfoErrors,
        setContactInfoErrors,
      }}
    >
      <Layout>
        {!isSubmissionCreated && <CurrentStepComponent />}
        {isSubmissionCreated && <SubmissionCreated key={submission?.id ?? "result"} />}
      </Layout>

      {["local", "test"].includes(import.meta.env.VITE_ENV) && <DevResultScreenSwitcher />}

      <Snackbar open={showError} autoHideDuration={5000} onClose={handleCloseError}>
        <Alert
          onClose={handleCloseError}
          severity="error"
          variant="filled"
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </AppContext.Provider>
  );
}

export default App;
